import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth, realTimeDb } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, get, set, update } from 'firebase/database';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { PLAN_CONFIG, PLAN_ORDER, DEFAULT_PLAN, getPlanLimit, isUnlimitedLimit, formatLimitLabel } from '../config/planConfig';
import SubscriptionService from '../services/subscriptionService';
import FormImportExport from './FormImportExport';

const LABEL_MAP_KEY = '__labelMap';

const Dashboard = () => {
  const [fields, setFields] = useState([]);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [dropdownOptions, setDropdownOptions] = useState([{ label: '', note: '' }]);
  const [queryApiUrl, setQueryApiUrl] = useState('');
  const [queryApiHeaders, setQueryApiHeaders] = useState('');
  const [queryDataPath, setQueryDataPath] = useState('');
  const [queryDisplayField, setQueryDisplayField] = useState('');
  const [queryValueField, setQueryValueField] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('default');
  const [formType, setFormType] = useState('single');
  const [variants, setVariants] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [showReferenceNumber, setShowReferenceNumber] = useState(true);
  const [requireAllFields, setRequireAllFields] = useState(true);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [hasStoredPassword, setHasStoredPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'edit', 'submissions', 'account'
  const [searchTerm, setSearchTerm] = useState('');
  const [forms, setForms] = useState([]);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const [creationMode, setCreationMode] = useState('');
  const isCreatingFormRef = useRef(false);
  const [userPlan, setUserPlan] = useState({
    plan: DEFAULT_PLAN,
    monthlyLimit: getPlanLimit(DEFAULT_PLAN),
    usageCount: 0,
    usageMonth: '',
    billingStatus: 'active',
    subscriptionId: null
  });
  const [planLoading, setPlanLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState('');
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api-ebz57wajla-uc.a.run.app';
  console.log('Using API URL:', apiBaseUrl); // Debug log
  const [paymentConfig, setPaymentConfig] = useState({});
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(true);
  const [limitOverlayDismissed, setLimitOverlayDismissed] = useState(false);
  
  // PayFast payment settings for individual forms (per variant for stackable forms)
  const [variantPaymentSettings, setVariantPaymentSettings] = useState({});

  // Import/Export modal state
  const [showImportExportModal, setShowImportExportModal] = useState(false);
  const [importExportSelectedFormId, setImportExportSelectedFormId] = useState(null);

  const normaliseVariants = (rawVariants) => {
    if (!rawVariants) {
      return [];
    }

    let variantList = [];

    if (Array.isArray(rawVariants)) {
      variantList = rawVariants;
    } else if (typeof rawVariants === 'object') {
      variantList = Object.entries(rawVariants).map(([key, value]) => ({ id: key, ...(value || {}) }));
    }

    return variantList.map((variant, index) => ({
      id: variant?.id || variant?.key || `variant-${index}-${Date.now()}`,
      name: variant?.name || `Option ${index + 1}`,
      fields: Array.isArray(variant?.fields) ? variant.fields : []
    }));
  };

  const variantsToStorage = (variantList) =>
    variantList.reduce((acc, variant) => {
      if (!variant?.id) {
        return acc;
      }
      acc[variant.id] = {
        id: variant.id,
        name: variant.name,
        fields: Array.isArray(variant.fields) ? variant.fields : []
      };
      return acc;
    }, {});

  const limitInfo = useMemo(() => {
    const planKey = userPlan.plan || DEFAULT_PLAN;
    const planMeta = PLAN_CONFIG[planKey] || PLAN_CONFIG[DEFAULT_PLAN];
    const effectiveLimit = userPlan.monthlyLimit !== undefined ? userPlan.monthlyLimit : getPlanLimit(planKey);
    const unlimited = isUnlimitedLimit(effectiveLimit) || isUnlimitedLimit(planMeta.limit);
    const limitNumeric = unlimited ? Number.POSITIVE_INFINITY : Math.max(Number(effectiveLimit) || 0, 0);
    const usageCount = userPlan.usageCount || 0;
    const reached = !planLoading && !unlimited && usageCount >= limitNumeric;

    return {
      planKey,
      planMeta,
      effectiveLimit,
      unlimited,
      limitNumeric,
      usageCount,
      reached,
      usageMonth: userPlan.usageMonth
    };
  }, [planLoading, userPlan]);

  useEffect(() => {
    if (!limitInfo.reached) {
      setLimitOverlayDismissed(false);
    }
  }, [limitInfo.reached]);

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('We could not log you out. Please try again.');
    }
  };

  const hashPassword = async (password) => {
    if (!window.crypto || !window.crypto.subtle) {
      return Array.from(password)
        .map((char) => char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('');
    }
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setUser(authUser);
        setPlanLoading(true);
        fetchUserProfile(authUser);
        fetchAllForms(authUser);
        fetchSubmissions(authUser.uid);
        fetchPaymentConfig();
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Check for payment return and auto-navigate to account section
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle tier upgrade success
    if (urlParams.get('upgrade') === 'success') {
      const tier = urlParams.get('tier');
      setCurrentView('account');
      setTimeout(() => {
        alert(`✅ Successfully upgraded to ${tier || 'new tier'}! Your account has been updated.`);
        refreshUserPlan(); // Refresh user data
      }, 1000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle tier upgrade cancellation
    if (urlParams.get('upgrade') === 'cancelled') {
      setCurrentView('account');
      setTimeout(() => {
        alert('⚠️ Upgrade cancelled. You can try again anytime.');
      }, 1000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (urlParams.get('payment') === 'return') {
      setCurrentView('account');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle PayFast FREE subscription returns
    if (urlParams.get('subscription') === 'free_created') {
      setCurrentView('account');
      // Show success message for FREE subscription setup
      setTimeout(() => {
        alert('✅ Your FREE PayFast account has been set up successfully! You can now upgrade to paid plans anytime.');
      }, 1000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (urlParams.get('subscription') === 'free_cancelled') {
      // Handle cancelled FREE subscription setup
      setTimeout(() => {
        alert('⚠️ PayFast account setup was cancelled. You can set it up later from your account page.');
      }, 1000);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (user && currentView === 'dashboard') {
      setPlanLoading(true);
      fetchUserProfile(user);
    }
  }, [user, currentView]);

  // Automatically refresh user profile when the window/tab regains focus or becomes visible.
  useEffect(() => {
    const onFocus = () => {
      if (user && currentView === 'dashboard') {
        // small delay to allow IPN to be processed server-side
        setTimeout(() => fetchUserProfile(user), 1500);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onFocus();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, currentView]);

  const fetchPaymentConfig = async () => {
    try {
      const configRef = ref(realTimeDb, 'settings/payfast');
      const snapshot = await get(configRef);
      if (snapshot.exists()) {
        setPaymentConfig(snapshot.val());
      } else {
        setPaymentConfig({});
      }
    } catch (error) {
      console.error('Error fetching payment config:', error);
    } finally {
      setPaymentConfigLoading(false);
    }
  };

  const fetchUserProfile = async (authUser) => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    try {
      const profileRef = ref(realTimeDb, `users/${authUser.uid}`);
      const snapshot = await get(profileRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const planKey = PLAN_CONFIG[data.plan] ? data.plan : DEFAULT_PLAN;
        const planLimit = data.monthlyLimit !== undefined ? data.monthlyLimit : getPlanLimit(planKey);
        const usageData = data.usage || {};

        let usageMonth = usageData.month || currentMonth;
        let usageCount = usageData.count || 0;

        const updates = {};

        if (usageMonth !== currentMonth) {
          usageMonth = currentMonth;
          usageCount = 0;
          updates['usage/month'] = usageMonth;
          updates['usage/count'] = usageCount;
        }

        if (!data.plan || planKey !== data.plan) {
          updates.plan = planKey;
        }

        if (data.monthlyLimit === undefined) {
          updates.monthlyLimit = planLimit;
        }

        if (!usageData.month) {
          updates['usage/month'] = usageMonth;
        }

        if (usageData.count === undefined) {
          updates['usage/count'] = usageCount;
        }

        if (data.billingStatus === undefined) {
          updates.billingStatus = 'active';
        }

        setUserPlan({
          plan: planKey,
          monthlyLimit: planLimit,
          usageCount,
          usageMonth,
          billingStatus: data.billingStatus || 'active',
          subscriptionId: data.billing?.payfast?.subscriptionId || data.subscriptionId || null
        });

        if (Object.keys(updates).length > 0) {
          await update(profileRef, updates);
        }
      } else {
        const defaultLimit = getPlanLimit(DEFAULT_PLAN);
        const defaultProfile = {
          email: authUser.email || '',
          plan: DEFAULT_PLAN,
          monthlyLimit: defaultLimit,
          usage: {
            month: currentMonth,
            count: 0
          },
          billingStatus: 'active'
        };

        await set(profileRef, defaultProfile);

        setUserPlan({
          plan: DEFAULT_PLAN,
          monthlyLimit: defaultLimit,
          usageCount: 0,
          usageMonth: currentMonth,
          billingStatus: 'active'
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setPlanLoading(false);
    }
  };

  const fetchAllForms = async (authUser) => {
    try {
      console.log('Dashboard - Starting form fetch for user:', authUser.uid);
      
      // First, check for legacy single form and migrate it
      await migrateLegacyForm(authUser);
      
      const formsRef = ref(realTimeDb, `users/${authUser.uid}/forms`);
      console.log('Dashboard - Fetching forms from path:', `users/${authUser.uid}/forms`);
      const snapshot = await get(formsRef);

      if (snapshot.exists()) {
        const formsData = snapshot.val();
        console.log('Dashboard - Forms data found:', formsData);
        console.log('Dashboard - User ID:', authUser.uid);
        console.log('Dashboard - Forms path:', `users/${authUser.uid}/forms`);
        const formsList = Object.entries(formsData).map(([id, form]) => ({
          id,
          ...form,
          isPublic: form.isPublic !== undefined ? form.isPublic : true,
          accessPasswordHash: form.accessPasswordHash || '',
          formType: form.formType || 'single',
          requireAllFields: form.requireAllFields !== undefined ? form.requireAllFields : true
        }));
        console.log('Dashboard - Processed forms list:', formsList);
        console.log('Dashboard - Forms count:', formsList.length);
        setForms(formsList);
      } else {
        console.log('Dashboard - No forms found in new structure');
        setForms([]);
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setLoading(false);
    }
  };

  // Upgrade to specific tier using new single subscription system
  const upgradeTier = async (targetTier) => {
    if (!user) return;
    
    setProcessingPlan(targetTier);
    
    try {
      if (targetTier === 'FREE') {
        // Downgrade to FREE
        const response = await fetch(`https://api-ebz57wajla-uc.a.run.app/user/downgrade-to-free`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.uid,
            email: user.email
          }),
        });

        if (response.ok) {
          const result = await response.json();
          alert('Successfully downgraded to FREE tier!');
          await refreshUserPlan();
        } else {
          throw new Error('Failed to downgrade to FREE tier');
        }
      } else {
        // Upgrade to paid tier - this will redirect to PayFast
        const response = await fetch(`https://api-ebz57wajla-uc.a.run.app/user/upgrade-tier`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.uid,
            email: user.email,
            targetTier: targetTier
          }),
        });

        if (response.ok) {
          // Response is HTML that will redirect to PayFast
          const htmlContent = await response.text();
          const newWindow = window.open('', '_blank');
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          throw new Error('Failed to initiate tier upgrade');
        }
      }
    } catch (error) {
      console.error('Error upgrading tier:', error);
      alert('Failed to upgrade tier. Please try again.');
    } finally {
      setProcessingPlan('');
    }
  };

  // Get current tier info using new backend
  const getCurrentTierInfo = async () => {
    if (!user) return null;
    
    try {
      const response = await fetch(`https://api-ebz57wajla-uc.a.run.app/user/current-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      }
    } catch (error) {
      console.error('Error getting tier info:', error);
    }
    return null;
  };

  const initiatePlanUpgrade = async (targetPlan) => {
    if (!user || !PLAN_CONFIG[targetPlan]) return;
    if (targetPlan === userPlan.plan) {
      alert('You are already on this plan.');
      return;
    }

    try {
      setProcessingPlan(targetPlan);
      const planMeta = PLAN_CONFIG[targetPlan];

      // Free tier – downgrade locally without PayFast
      if ((planMeta.priceMonthly || 0) === 0) {
        const profileRef = ref(realTimeDb, `users/${user.uid}`);
        await update(profileRef, {
          plan: targetPlan,
          monthlyLimit: planMeta.limit,
          billingStatus: 'active'
        });
        setUserPlan((prev) => ({
          ...prev,
          plan: targetPlan,
          monthlyLimit: planMeta.limit,
          billingStatus: 'active'
        }));
        alert('Your plan has been switched to the Free tier.');
        return;
      }

      if ((planMeta.priceMonthly || 0) > 0) {
        const requiredFields = ['merchantId', 'merchantKey'];
        const missingFields = requiredFields.filter((field) => !paymentConfig?.[field]);
        if (missingFields.length > 0) {
          alert(`Payment configuration is incomplete (${missingFields.join(', ')}). Please ask an admin to update the PayFast settings.`);
          return;
        }
      }

      const response = await fetch(`${apiBaseUrl}/payfast/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: targetPlan,
          userId: user.uid,
          email: user.email,
          returnUrl: `${window.location.origin}/Accounts`,
          cancelUrl: `${window.location.origin}/Dashboard`,
          ...paymentConfig
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Check if response is HTML (PayFast form) or JSON (error/free plan)
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('text/html')) {
        // Create a new window/tab to display the PayFast form
        const htmlContent = await response.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          // Fallback: create a blob URL and navigate to it
          const blob = new Blob([htmlContent], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.location.href = url;
        }
      } else {
        // Handle JSON response
        const payload = await response.json();
        if (payload.message) {
          alert(payload.message);
        } else if (payload.redirectUrl) {
          window.location.href = payload.redirectUrl;
        } else {
          alert('Could not start the payment. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error initiating PayFast payment:', error);
      alert('We could not launch the upgrade checkout. Please try again in a moment.');
    } finally {
      setProcessingPlan('');
    }
  };

  const cancelSubscription = async () => {
    if (!user?.uid || !userPlan?.subscriptionId) {
      alert('No active subscription found to cancel.');
      return;
    }

    const confirmCancel = window.confirm(
      'Are you sure you want to cancel your subscription? This will downgrade your account to the Free plan at the end of your current billing period.'
    );

    if (!confirmCancel) return;

    try {
      const response = await fetch(`${apiBaseUrl}/payfast/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.uid,
          subscriptionId: userPlan.subscriptionId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();
      if (result.success) {
        const message = result.payfastCancelled 
          ? 'Your subscription has been cancelled successfully with PayFast. You will not be charged again.'
          : 'Your subscription has been cancelled in our system. If charges continue, please contact PayFast directly.';
        
        alert(message);
        
        // Update local state
        setUserPlan(prev => ({
          ...prev,
          billingStatus: 'cancelled'
        }));
      } else {
        alert(result.message || 'Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please contact support.');
    }
  };

  const refreshUserPlan = async () => {
    if (!user?.uid) return;
    
    try {
      setPlanLoading(true);
      
      // First get current user plan from Firebase
      const currentPlan = await SubscriptionService.getUserPlan(user.uid);
      if (currentPlan) {
        setUserPlan(currentPlan);
        
        // If user has a subscription ID, check status with PayFast
        if (currentPlan.subscriptionId) {
          try {
            const statusResult = await SubscriptionService.checkSubscriptionStatus(
              user.uid, 
              currentPlan.subscriptionId
            );
            
            // Update local state with fresh PayFast status
            if (statusResult.success) {
              setUserPlan(prev => ({
                ...prev,
                billingStatus: statusResult.status || prev.billingStatus,
                lastPaymentDate: statusResult.lastPaymentDate || prev.lastPaymentDate,
                lastPaymentAmount: statusResult.lastPaymentAmount || prev.lastPaymentAmount
              }));
            }
          } catch (statusError) {
            console.warn('Could not check PayFast status:', statusError.message);
            // Continue with cached data
          }
        }
      } else {
        // Fallback to old method
        await fetchUserProfile(user);
      }
      
      alert('Account information refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing user plan:', error);
      alert('Failed to refresh account information.');
    } finally {
      setPlanLoading(false);
    }
  };

  const renderLimitOverlay = () => {
    if (!limitInfo.reached || limitOverlayDismissed) {
      return null;
    }

    const currentPrice = PLAN_CONFIG[limitInfo.planKey]?.priceMonthly || 0;
    const upgradeTarget = PLAN_ORDER.find((planId) => (PLAN_CONFIG[planId]?.priceMonthly || 0) > currentPrice);
    const nextPlan = upgradeTarget ? PLAN_CONFIG[upgradeTarget] : null;
    const limitLabel = formatLimitLabel(limitInfo.effectiveLimit);

    return (
      <div className="usage-overlay">
        <div className="usage-overlay-card">
          <h2>You're out of submissions</h2>
          <p>
            You have used all {limitLabel} submissions included with your {limitInfo.planMeta.name} plan.
            Submissions pause until the allowance resets at the start of the next billing cycle.
          </p>
          {nextPlan ? (
            <button
              type="button"
              onClick={() => initiatePlanUpgrade(upgradeTarget)}
              disabled={processingPlan === upgradeTarget}
              className="usage-overlay-upgrade"
            >
              {processingPlan === upgradeTarget ? 'Redirecting…' : `Upgrade to ${nextPlan.name}`}
            </button>
          ) : (
            <p className="usage-overlay-footnote">
              You're already on the highest available plan. Contact support if you need a higher allowance.
            </p>
          )}
          <button
            type="button"
            onClick={() => setLimitOverlayDismissed(true)}
            className="usage-overlay-dismiss"
          >
            Maybe later
          </button>
          <p className="usage-overlay-footnote">
            Need to keep collecting responses? Upgrade now or wait for the next monthly reset.
          </p>
        </div>
      </div>
    );
  };

  const renderPlanOverview = () => {
    if (planLoading) {
      return (
        <div className="plan-card" style={{ marginBottom: '25px' }}>
          <h3>Account Plan</h3>
          <p>Loading plan details...</p>
        </div>
      );
    }

    const planKey = userPlan.plan || DEFAULT_PLAN;
    const planMeta = PLAN_CONFIG[planKey] || PLAN_CONFIG[DEFAULT_PLAN];
    const effectiveLimit = userPlan.monthlyLimit !== undefined ? userPlan.monthlyLimit : getPlanLimit(planKey);
    const unlimited = isUnlimitedLimit(effectiveLimit) || isUnlimitedLimit(planMeta.limit);
    const usageCount = userPlan.usageCount || 0;
    const displayLimitLabel = unlimited ? 'Unlimited' : formatLimitLabel(effectiveLimit);
    const limitNumeric = unlimited ? Number.POSITIVE_INFINITY : Math.max(Number(effectiveLimit) || 0, 1);
    const usagePercent = unlimited ? 0 : Math.min(100, Math.round((usageCount / limitNumeric) * 100));
    const reachedLimit = !unlimited && usageCount >= limitNumeric;

    return (
      <div className="plan-card" style={{ marginBottom: '25px', backgroundColor: '#1f2933', color: 'white', borderRadius: '16px', padding: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '20px' }}>
          <div>
            <h3 style={{ margin: 0 }}>{planMeta.name} Plan</h3>
            <p style={{ margin: '6px 0 0 0', color: '#cbd5e1' }}>{planMeta.priceLabel}</p>
            <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: reachedLimit ? '#fb7185' : '#e2e8f0' }}>
              {unlimited ? `${usageCount} submissions logged this month` : `${usageCount} of ${displayLimitLabel} submissions used`}
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>Resets monthly · Status: {userPlan.billingStatus}</p>
          </div>

          <div style={{ flex: '1 1 250px', maxWidth: '350px' }}>
            {!unlimited && (
              <div style={{ backgroundColor: '#111827', borderRadius: '10px', padding: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>
                  <span>Usage this month</span>
                  <span>{usagePercent}%</span>
                </div>
                <div style={{ backgroundColor: '#1f2937', borderRadius: '999px', overflow: 'hidden', height: '10px' }}>
                  <div style={{ width: `${usagePercent}%`, height: '100%', background: reachedLimit ? '#fb7185' : '#38bdf8', transition: 'width 0.3s ease' }} />
                </div>
              </div>
            )}

            {reachedLimit && (
              <p style={{ color: '#fb7185', fontSize: '12px', marginTop: '10px' }}>
                You have reached your submission allowance. Upgrade to continue collecting responses.
              </p>
            )}
          </div>
        </div>

        <div style={{ marginTop: '20px', display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {PLAN_ORDER.map((planId) => {
            const plan = PLAN_CONFIG[planId];
            const isCurrent = planId === planKey;
            const currentPrice = PLAN_CONFIG[planKey]?.priceMonthly || 0;
            const isUpgrade = plan.priceMonthly > currentPrice;
            const isDowngrade = plan.priceMonthly < currentPrice;
            const buttonLabel = isCurrent
              ? 'Current Plan'
              : processingPlan === planId
                ? 'Preparing...'
                : isUpgrade
                  ? 'Upgrade'
                  : isDowngrade
                    ? 'Downgrade'
                    : 'Switch';
            const disabled = planLoading || processingPlan === planId || isCurrent;
            return (
              <div key={planId} style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px', border: isCurrent ? '1px solid #38bdf8' : '1px solid transparent' }}>
                <h4 style={{ margin: 0, color: 'white' }}>{plan.name}</h4>
                <p style={{ margin: '6px 0', color: '#cbd5e1', fontSize: '13px' }}>{plan.priceLabel}</p>
                <p style={{ margin: '6px 0', color: '#94a3b8', fontSize: '12px' }}>
                  {isUnlimitedLimit(plan.limit) ? 'Unlimited submissions per month' : `${formatLimitLabel(plan.limit)} submissions per month`}
                </p>
                <p style={{ margin: '6px 0', color: '#94a3b8', fontSize: '12px' }}>
                  📋 {plan.formLimit} forms
                </p>
                <button
                  onClick={() => initiatePlanUpgrade(planId)}
                  disabled={disabled}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isCurrent ? '#334155' : '#38bdf8',
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  {buttonLabel}
                </button>
              </div>
            );
          })}
        </div>

        {/* Subscription Management Section */}
        {userPlan.subscriptionId && userPlan.plan !== DEFAULT_PLAN && userPlan.billingStatus === 'active' && (
          <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#111827', borderRadius: '12px', border: '1px solid #374151' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#f3f4f6', fontSize: '16px' }}>
              🔄 Subscription Management
            </h4>
            <p style={{ margin: '0 0 16px 0', color: '#9ca3af', fontSize: '14px' }}>
              Subscription ID: {userPlan.subscriptionId}
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={cancelSubscription}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #dc2626',
                  backgroundColor: 'transparent',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#dc2626';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc2626';
                }}
              >
                ❌ Cancel Subscription
              </button>
              <button
                onClick={() => {
                  const updateUrl = `https://www.payfast.co.za/eng/recurring/update/${userPlan.subscriptionId}?return=${encodeURIComponent(window.location.href)}`;
                  window.open(updateUrl, '_blank', 'width=800,height=600');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #059669',
                  backgroundColor: 'transparent',
                  color: '#059669',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#059669';
                }}
              >
                💳 Update Payment Method
              </button>
            </div>
            <p style={{ margin: '12px 0 0 0', color: '#6b7280', fontSize: '12px' }}>
              💡 Tip: Cancellation takes effect at the end of your current billing period. You'll keep access until then.
            </p>
          </div>
        )}
      </div>
    );
  };

  const migrateLegacyForm = async (authUser) => {
    try {
      console.log('Dashboard - Checking for legacy form at:', `users/${authUser.uid}/form`);
      
      // Check if there's a legacy form at the old path
      const legacyFormRef = ref(realTimeDb, `users/${authUser.uid}/form`);
      const legacySnapshot = await get(legacyFormRef);
      
      if (legacySnapshot.exists()) {
        const legacyFormData = legacySnapshot.val();
        console.log('Dashboard - Legacy form found:', legacyFormData);
        
        // Check if this specific legacy form has already been migrated
        const formsRef = ref(realTimeDb, `users/${authUser.uid}/forms`);
        const formsSnapshot = await get(formsRef);
        
        let legacyAlreadyMigrated = false;
        if (formsSnapshot.exists()) {
          const existingForms = formsSnapshot.val();
          // Check if any existing form has the same title or matching signature as the legacy form
          legacyAlreadyMigrated = Object.values(existingForms).some(form => 
            form.title === legacyFormData.title ||
            form.signature === legacyFormData.signature || 
            form.isLegacyMigration === true
          );
        }
        
        if (!legacyAlreadyMigrated) {
          console.log('Dashboard - Migrating legacy form...');
          
          // Migrate the legacy form with a special identifier
          const legacyFormId = 'legacy-' + Date.now();
          const migratedForm = {
            ...legacyFormData,
            createdAt: legacyFormData.createdAt || new Date().toISOString(),
            signature: legacyFormData.signature || Math.floor(10000000 + Math.random() * 90000000),
            title: legacyFormData.title || 'srsa mk2 flowbuilder 1st',
            isPublic: legacyFormData.isPublic !== undefined ? legacyFormData.isPublic : true,
            accessPasswordHash: legacyFormData.accessPasswordHash || '',
            isLegacyMigration: true, // Mark this as a migrated form
            originalLegacyForm: true
          };
          
          // Save to new location
      const newFormRef = ref(realTimeDb, `users/${authUser.uid}/forms/${legacyFormId}`);
      await set(newFormRef, migratedForm);
          
      console.log('Dashboard - Legacy form migrated successfully to:', `users/${authUser.uid}/forms/${legacyFormId}`);
          
        } else {
          console.log('Dashboard - Legacy form already migrated');
        }
      }
    } catch (error) {
      console.error("Error migrating legacy form:", error);
    } finally {
      const legacyCleanupRef = ref(realTimeDb, `users/${authUser.uid}/form`);
      try {
        await set(legacyCleanupRef, null);
      } catch (cleanupError) {
        console.error("Error clearing legacy form path:", cleanupError);
      }
    }
  };

  const fetchFormData = async (formId) => {
    if (!user || !formId) return;
    
    try {
      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${formId}`);
      const snapshot = await get(formRef);

      if (snapshot.exists()) {
        const formData = snapshot.val();
        const detectedType = formData.formType || 'single';
        const normalisedVariants = normaliseVariants(formData.variants);

        if (detectedType === 'stackable') {
          const storedVariantsShape = formData.variants;
          const shouldRewriteVariants =
            !storedVariantsShape ||
            Array.isArray(storedVariantsShape) ||
            Object.keys(variantsToStorage(normalisedVariants)).some(
              (key) => !storedVariantsShape[key]
            );

          if (shouldRewriteVariants && normalisedVariants.length) {
            try {
              const variantsRef = ref(realTimeDb, `users/${user.uid}/forms/${formId}/variants`);
              await set(variantsRef, variantsToStorage(normalisedVariants));
            } catch (err) {
              console.warn('Unable to normalise variants immediately:', err);
            }
          }
        }

        setFormType(detectedType);
        setVariants(normalisedVariants);

        if (detectedType === 'stackable' && normalisedVariants.length > 0) {
          const preferredVariantId = normalisedVariants.find((variant) => variant.id === activeVariantId)?.id
            || normalisedVariants[0].id;
          const activeVariant = normalisedVariants.find((variant) => variant.id === preferredVariantId) || normalisedVariants[0];
          setActiveVariantId(activeVariant?.id || null);
          setFields(activeVariant?.fields || []);
        } else {
          setActiveVariantId(null);
          setFields(formData.fields || []);
        }

        setWebhookUrl(formData.webhookUrl || '');
        setFormTitle(formData.title || 'Untitled Form');
        setSelectedStyle(formData.style || 'default');
        setClientEmail(formData.clientEmail || '');
        setShowReferenceNumber(formData.showReferenceNumber !== undefined ? formData.showReferenceNumber : true);
        setRequireAllFields(formData.requireAllFields !== undefined ? formData.requireAllFields : true);
        
        // Load PayFast payment settings per variant
        const paymentSettings = {};
        if (formData.variants) {
          Object.keys(formData.variants).forEach(variantId => {
            const variant = formData.variants[variantId];
            paymentSettings[variantId] = {
              enabled: variant?.paymentEnabled || false,
              url: variant?.paymentUrl || ''
            };
          });
        }
        // For non-stackable forms, use global payment settings
        if (!formData.variants && formData.paymentEnabled) {
          paymentSettings['global'] = {
            enabled: formData.paymentEnabled || false,
            url: formData.paymentUrl || ''
          };
        }
        setVariantPaymentSettings(paymentSettings);
        
        const passwordHash = formData.accessPasswordHash || '';
        const formIsPublic = formData.isPublic !== undefined ? formData.isPublic : true;
        setIsPasswordProtected(!formIsPublic && Boolean(passwordHash));
        setHasStoredPassword(Boolean(passwordHash));
        setPasswordInput('');
        setCurrentFormId(formId);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
    }
  };

  const createNewForm = async (options = {}) => {
    if (!user || isCreatingFormRef.current) return;

    const requestedType = options.formType === 'stackable' ? 'stackable' : 'single';
    const variantSeed = Date.now().toString();
    const defaultVariantId = `variant-${variantSeed}`;
    const initialVariants = requestedType === 'stackable'
      ? [{ id: defaultVariantId, name: 'Subform 1', fields: [] }]
      : [];

    try {
      isCreatingFormRef.current = true;
      setCreationMode(requestedType);
      setIsCreatingForm(true);

      const newFormId = Date.now().toString(); // Simple ID generation
      const newForm = {
        title: 'Untitled Form',
        fields: requestedType === 'single' ? [] : null,
        webhookUrl: '',
        style: 'default',
        clientEmail: '',
        showReferenceNumber: true,
        requireAllFields: true,
        formType: requestedType,
        variants: requestedType === 'stackable' ? variantsToStorage(initialVariants) : [],
        isPublic: true,
        accessPasswordHash: '',
        createdAt: new Date().toISOString(),
        signature: Math.floor(10000000 + Math.random() * 90000000) // 8-digit signature
      };

      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${newFormId}`);
      await set(formRef, newForm);

      // Update local state while keeping ids unique to avoid transient duplicates
      setForms(prev => {
        if (prev.some(form => form.id === newFormId)) {
          return prev;
        }
        return [...prev, { id: newFormId, ...newForm }];
      });

      // Load the new form for editing
      setFields(requestedType === 'stackable' ? (initialVariants[0]?.fields || []) : []);
      setWebhookUrl('');
      setFormTitle('Untitled Form');
      setSelectedStyle('default');
      setFormType(requestedType);
      setVariants(initialVariants);
      setActiveVariantId(requestedType === 'stackable' ? defaultVariantId : null);
      setClientEmail('');
      setShowReferenceNumber(true);
      setIsPasswordProtected(false);
      setHasStoredPassword(false);
      setPasswordInput('');
      setCurrentFormId(newFormId);
      setCurrentView('edit');
    } catch (error) {
      console.error("Error creating new form:", error);
      alert("Failed to create new form. Please try again.");
    } finally {
      isCreatingFormRef.current = false;
      setCreationMode('');
      setIsCreatingForm(false);
    }
  };

  const deleteForm = async (formId) => {
    if (!user || !formId) return;
    
    const confirmDelete = window.confirm('Are you sure you want to delete this form? This action cannot be undone.');
    if (!confirmDelete) return;

    try {
      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${formId}`);
      await set(formRef, null); // Delete the form
      
      // Update local state
      setForms(prev => prev.filter(form => form.id !== formId));
      
      // If we're currently editing this form, clear the editor
      if (currentFormId === formId) {
        setCurrentFormId(null);
        setFields([]);
        setFormTitle('');
        setWebhookUrl('');
        setSelectedStyle('default');
        setClientEmail('');
        setCurrentView('dashboard');
      }
      
      alert('Form deleted successfully!');
    } catch (error) {
      console.error("Error deleting form:", error);
      alert("Failed to delete form. Please try again.");
    }
  };



  // Import/Export Functions
  const openImportExportModal = (formId = null) => {
    setImportExportSelectedFormId(formId);
    setShowImportExportModal(true);
  };

  const closeImportExportModal = () => {
    setShowImportExportModal(false);
    setImportExportSelectedFormId(null);
  };

  const handleFormsUpdated = async () => {
    // Refresh forms after import
    if (user) {
      await fetchAllForms(user);
    }
  };

  const fetchSubmissions = async (uid) => {
    try {
      const submissionsRef = ref(realTimeDb, `users/${uid}/submissions`);
      const snapshot = await get(submissionsRef);
      if (snapshot.exists()) {
        const submissionList = Object.entries(snapshot.val()).map(([id, sub]) => ({
          id,
          ...sub
        }));
        setSubmissions(submissionList);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  };

  const handleTitleChange = async (e) => {
    const newTitle = e.target.value;
    setFormTitle(newTitle);
    if (user && currentFormId) {
      try {
        const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);
        await update(formRef, { title: newTitle });
        
        // Update local forms state
        setForms(prev => prev.map(form => 
          form.id === currentFormId ? { ...form, title: newTitle } : form
        ));
      } catch (error) {
        console.error("Error updating form title:", error);
      }
    }
  };

  const updateWebhookUrl = async () => {
    if (user && currentFormId) {
      try {
        const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);
        await update(formRef, { webhookUrl });
        alert("Webhook URL saved successfully!");
      } catch (error) {
        console.error("Error saving webhook URL:", error);
      }
    }
  };

  const saveVariantPaymentSettings = async (variantId, enabled, url) => {
    if (user && currentFormId) {
      try {
        const variantRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}/variants/${variantId}`);
        await update(variantRef, { 
          paymentEnabled: enabled,
          paymentUrl: enabled ? url : ''
        });
        
        // Update local state
        setVariantPaymentSettings(prev => ({
          ...prev,
          [variantId]: { enabled, url: enabled ? url : '' }
        }));
        
        alert("Payment settings saved successfully!");
      } catch (error) {
        console.error("Error saving payment settings:", error);
        alert("Failed to save payment settings. Please try again.");
      }
    }
  };

  const saveAccessSettings = async () => {
    if (!user || !currentFormId) return;

    try {
      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);
      const updates = { isPublic: !isPasswordProtected };
      let message = '';

      if (isPasswordProtected) {
        if (passwordInput.trim()) {
          const hashedPassword = await hashPassword(passwordInput.trim());
          updates.accessPasswordHash = hashedPassword;
          setHasStoredPassword(true);
          message = 'Password updated and protection enabled.';
        } else if (!hasStoredPassword) {
          alert('Please enter a password to protect this form.');
          return;
        } else {
          message = 'Password protection enabled.';
        }
      } else {
        updates.accessPasswordHash = '';
        setHasStoredPassword(false);
        message = 'Form access set to public.';
      }

      await update(formRef, updates);

      setForms(prev => prev.map(form => {
        if (form.id !== currentFormId) return form;
        const updatedForm = { ...form, isPublic: updates.isPublic };
        if (updates.accessPasswordHash !== undefined) {
          updatedForm.accessPasswordHash = updates.accessPasswordHash;
        }
        return updatedForm;
      }));

      setPasswordInput('');
      alert(message);
    } catch (error) {
      console.error('Error saving access settings:', error);
      alert('Failed to update form access settings. Please try again.');
    }
  };

  const updateReferenceNumberSetting = async () => {
    if (user && currentFormId) {
      try {
        const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);
        await update(formRef, { showReferenceNumber });
        alert("Reference number setting saved successfully!");
      } catch (error) {
        console.error("Error saving reference number setting:", error);
      }
    }
  };

  const updateRequireAllFieldsSetting = async (nextValue) => {
    setRequireAllFields(nextValue);

    if (!user || !currentFormId) {
      return;
    }

    try {
      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);
      await update(formRef, { requireAllFields: nextValue });

      setForms(prev => prev.map(form =>
        form.id === currentFormId ? { ...form, requireAllFields: nextValue } : form
      ));
    } catch (error) {
      console.error('Error saving field requirement setting:', error);
      alert('Failed to update the field requirement rule. Please try again.');
    }
  };

    const syncVariantFieldsState = (variantId, updatedFields) => {
      if (!variantId) {
        return;
      }

      setVariants(prev => prev.map(variant =>
        variant.id === variantId ? { ...variant, fields: updatedFields } : variant
      ));
    };

    const persistFields = async (updatedFields) => {
      if (!user || !currentFormId) {
        return;
      }

      const basePath = `users/${user.uid}/forms/${currentFormId}`;

      if (formType === 'stackable') {
        if (!activeVariantId) {
          alert('Please add a subform first.');
          return;
        }

        const variantFieldsRef = ref(realTimeDb, `${basePath}/variants/${activeVariantId}/fields`);
        await set(variantFieldsRef, updatedFields);
        syncVariantFieldsState(activeVariantId, updatedFields);
      } else {
        const fieldsRef = ref(realTimeDb, `${basePath}/fields`);
        await set(fieldsRef, updatedFields);
      }
    };

    const persistVariants = async (updatedVariants) => {
      setVariants(updatedVariants);

      if (!user || !currentFormId) {
        return;
      }

      const variantsRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}/variants`);
      await set(variantsRef, variantsToStorage(updatedVariants));
    };

    const handleFormTypeChange = async (nextType) => {
      if (nextType === formType) {
        return;
      }

      if (!user || !currentFormId) {
        setFormType(nextType);

        if (nextType === 'stackable') {
          const generatedId = `variant-${Date.now()}`;
          const defaultVariant = {
            id: generatedId,
            name: 'Subform 1',
            fields: [...fields]
          };
          setVariants([defaultVariant]);
          setActiveVariantId(generatedId);
        } else {
          setVariants([]);
          setActiveVariantId(null);
        }
        return;
      }

      const formRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`);

      try {
        if (nextType === 'single') {
          await update(formRef, {
            formType: 'single',
            fields,
            variants: null
          });
          setVariants([]);
          setActiveVariantId(null);
        } else {
          let preparedVariants = variants;
          if (!preparedVariants.length) {
            const generatedId = `variant-${Date.now()}`;
            preparedVariants = [{
              id: generatedId,
              name: 'Subform 1',
              fields: [...fields]
            }];
          }

          await update(formRef, {
            formType: 'stackable',
            variants: variantsToStorage(preparedVariants),
            fields: null
          });

          setVariants(preparedVariants);
          setActiveVariantId(preparedVariants[0]?.id || null);
          setFields(preparedVariants[0]?.fields || []);
        }

        setFormType(nextType);
        setForms(prev => prev.map(form =>
          form.id === currentFormId ? { ...form, formType: nextType } : form
        ));
      } catch (error) {
        console.error('Error updating form structure:', error);
        alert('Could not update the form structure. Please try again.');
      }
    };

    const handleVariantSelect = (variantId) => {
      if (variantId === activeVariantId) {
        return;
      }

      const variant = variants.find((entry) => entry.id === variantId);
      setActiveVariantId(variantId);
      setFields(variant?.fields || []);
      resetFieldInputs();
    };

    const handleAddVariant = async () => {
  const proposedName = prompt('Name for the new subform?', `Subform ${variants.length + 1}`);
      if (proposedName === null) {
        return;
      }

      const trimmedName = proposedName.trim();
      if (!trimmedName) {
  alert('Please provide a valid subform name.');
        return;
      }

      const newVariant = {
        id: `variant-${Date.now()}`,
        name: trimmedName,
        fields: []
      };

      const updatedVariants = [...variants, newVariant];

      try {
        await persistVariants(updatedVariants);
        if (user && currentFormId) {
          await update(ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}`), { formType: 'stackable' });
        }
        setFormType('stackable');
        setActiveVariantId(newVariant.id);
        setFields([]);
        setForms(prev => prev.map(form =>
          form.id === currentFormId ? { ...form, formType: 'stackable' } : form
        ));
      } catch (error) {
  console.error('Error creating new subform:', error);
  alert('Could not add the new subform. Please try again.');
      }
    };

    const handleRenameVariant = async (variantId) => {
      const target = variants.find((entry) => entry.id === variantId);
      if (!target) {
        return;
      }

  const proposedName = prompt('Rename subform', target.name);
      if (proposedName === null) {
        return;
      }

      const trimmedName = proposedName.trim();
      if (!trimmedName) {
  alert('Subform name cannot be empty.');
        return;
      }

      const updatedVariants = variants.map((entry) =>
        entry.id === variantId ? { ...entry, name: trimmedName } : entry
      );

      try {
        await persistVariants(updatedVariants);
      } catch (error) {
  console.error('Error renaming subform:', error);
  alert('Could not rename the subform. Please try again.');
      }
    };

    const handleDeleteVariant = async (variantId) => {
      if (variants.length <= 1) {
  alert('At least one subform is required for stackable forms.');
        return;
      }

  const confirmed = window.confirm('Delete this subform? Its questions will be removed.');
      if (!confirmed) {
        return;
      }

      const updatedVariants = variants.filter((entry) => entry.id !== variantId);

      try {
        await persistVariants(updatedVariants);

        // Remove the variant node entirely to avoid orphan data
        if (user && currentFormId) {
          const variantRef = ref(realTimeDb, `users/${user.uid}/forms/${currentFormId}/variants/${variantId}`);
          await set(variantRef, null);
        }

        const nextActiveVariant = updatedVariants[0] || null;
        setActiveVariantId(nextActiveVariant?.id || null);
        setFields(nextActiveVariant?.fields || []);
      } catch (error) {
  console.error('Error deleting subform:', error);
  alert('Could not delete the subform. Please try again.');
      }
    };

  const addField = async () => {
    if (!newFieldLabel.trim() || !currentFormId) {
      return;
    }

    if (formType === 'stackable' && !activeVariantId) {
      alert('Please add a subform before adding fields.');
      return;
    }

    try {
      let fieldToAdd = {
        label: newFieldLabel,
        type: newFieldType
      };
      
      if (newFieldType === 'dropdown') {
        const cleanedOptions = dropdownOptions
          .filter((option) => option.label && option.label.trim() !== '')
          .map((option) => ({
            label: option.label.trim(),
            note: option.note ? option.note.trim() : ''
          }));

        if (!cleanedOptions.length) {
          alert('Please add at least one dropdown option with a label.');
          return;
        }

        fieldToAdd.options = cleanedOptions;
      } else if (newFieldType === 'query') {
        fieldToAdd.apiConfig = {
          url: queryApiUrl,
          headers: queryApiHeaders ? JSON.parse(queryApiHeaders) : {},
          dataPath: queryDataPath,
          displayField: queryDisplayField,
          valueField: queryValueField
        };
      }

      const updatedFields = [...fields, fieldToAdd];
      setFields(updatedFields);

      if (formType === 'stackable') {
        syncVariantFieldsState(activeVariantId, updatedFields);
      }

      await persistFields(updatedFields);
      resetFieldInputs();
    } catch (error) {
      console.error('Error adding field:', error);
    }
  };

  const resetFieldInputs = () => {
    setNewFieldLabel('');
    setNewFieldType('text');
    setDropdownOptions([{ label: '', note: '' }]);
    setQueryApiUrl('');
    setQueryApiHeaders('');
    setQueryDataPath('');
    setQueryDisplayField('');
    setQueryValueField('');
  };

  const addDropdownOption = () => {
    setDropdownOptions([...dropdownOptions, { label: '', note: '' }]);
  };

  const handleDropdownOptionChange = (index, key, value) => {
    const updatedOptions = [...dropdownOptions];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [key]: value
    };
    setDropdownOptions(updatedOptions);
  };

  const editField = (index) => {
    const fieldToEdit = fields[index];
    setNewFieldLabel(fieldToEdit.label);
    setNewFieldType(fieldToEdit.type);
    
    if (fieldToEdit.type === 'dropdown') {
      const normalizedOptions = (fieldToEdit.options || []).map((option) =>
        typeof option === 'string'
          ? { label: option, note: '' }
          : {
              label: option?.label || '',
              note: option?.note || ''
            }
      );
      setDropdownOptions(normalizedOptions.length ? normalizedOptions : [{ label: '', note: '' }]);
    } else if (fieldToEdit.type === 'query' && fieldToEdit.apiConfig) {
      setQueryApiUrl(fieldToEdit.apiConfig.url || '');
      setQueryApiHeaders(fieldToEdit.apiConfig.headers ? JSON.stringify(fieldToEdit.apiConfig.headers, null, 2) : '');
      setQueryDataPath(fieldToEdit.apiConfig.dataPath || '');
      setQueryDisplayField(fieldToEdit.apiConfig.displayField || '');
      setQueryValueField(fieldToEdit.apiConfig.valueField || '');
    }

    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);

    if (formType === 'stackable') {
      syncVariantFieldsState(activeVariantId, updatedFields);
    }
  };

  const removeField = async (index) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);

    if (formType === 'stackable') {
      syncVariantFieldsState(activeVariantId, updatedFields);
    }

    try {
      await persistFields(updatedFields);
    } catch (error) {
      console.error('Error removing field:', error);
    }
  };

  const moveField = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= fields.length) {
      return;
    }

    const updatedFields = [...fields];
    const [movedField] = updatedFields.splice(index, 1);
    updatedFields.splice(targetIndex, 0, movedField);
    setFields(updatedFields);

    if (formType === 'stackable') {
      syncVariantFieldsState(activeVariantId, updatedFields);
    }

    try {
      await persistFields(updatedFields);
    } catch (error) {
      console.error('Error reordering field:', error);
    }
  };

  // Function to generate PDF from submission data
  const generateSubmissionPDF = (submission) => {
    const pdf = new jsPDF();
    
    const submissionDate = new Date(submission.submittedAt);
    const fileDate = submissionDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).replace(/,/g, '');
    const referenceNumber = submission['Form Reference Number'] || 'Unknown';
    const filename = `${referenceNumber} ${fileDate}.pdf`;
    
    // PDF Header
    pdf.setFontSize(20);
    pdf.setTextColor(40, 40, 40);
    pdf.text(submission.formTitle || 'Form Submission', 20, 25);
    
    // Reference and Date Info
    pdf.setFontSize(12);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Reference: ${referenceNumber}`, 20, 35);
    pdf.text(`Submitted: ${submissionDate.toLocaleString()}`, 20, 42);
    
    // Draw a line separator
    pdf.setDrawColor(200, 200, 200);
    pdf.line(20, 50, 190, 50);
    
    // Form Data
    let yPosition = 65;
    pdf.setFontSize(11);
    pdf.setTextColor(40, 40, 40);
    
    const labelMap = submission[LABEL_MAP_KEY] || {};

    Object.entries(submission).forEach(([key, value]) => {
      if (!['id', 'submittedAt', 'formTitle', LABEL_MAP_KEY].includes(key)) {
        const displayKey = labelMap[key] || key;

        pdf.setFont(undefined, 'bold');
        pdf.text(`${displayKey}:`, 20, yPosition);
        
        pdf.setFont(undefined, 'normal');
        const valueText = value ? value.toString() : 'N/A';
        const splitText = pdf.splitTextToSize(valueText, 120);
        pdf.text(splitText, 50, yPosition);
        
        yPosition += splitText.length * 6 + 3;
        
        if (yPosition > 280) {
          pdf.addPage();
          yPosition = 25;
        }
      }
    });
    
    pdf.save(filename);
  };

  if (loading) return <p>Loading form data...</p>;

  const filteredForms = forms.filter(form => 
    form.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderDashboard = () => (
    <div className="main-content">
      <div className="main-header">
        <h2>📋 My Forms</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search forms..."
            className="search-box"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="filter-select">
            <option>All Forms</option>
            <option>Active</option>
            <option>Draft</option>
          </select>
        </div>
      </div>

      {renderPlanOverview()}

      <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px'}}>
        <button
          className="create-form-btn"
          onClick={() => {
            const planKey = userPlan.plan || DEFAULT_PLAN;
            const formLimit = PLAN_CONFIG[planKey]?.formLimit || 3;
            if (forms.length >= formLimit) {
              alert(`You have reached your form limit of ${formLimit} for the ${planKey} plan. Please upgrade to create more forms.`);
              return;
            }
            createNewForm({ formType: 'single' });
          }}
          disabled={isCreatingForm}
          style={{flex: '1 1 220px', marginBottom: 0}}
        >
          {isCreatingForm && creationMode !== 'stackable' ? 'Creating standard…' : '➕ Create Standard Form'}
        </button>
        <button
          className="create-form-btn"
          onClick={() => {
            const planKey = userPlan.plan || DEFAULT_PLAN;
            const formLimit = PLAN_CONFIG[planKey]?.formLimit || 3;
            if (forms.length >= formLimit) {
              alert(`You have reached your form limit of ${formLimit} for the ${planKey} plan. Please upgrade to create more forms.`);
              return;
            }
            createNewForm({ formType: 'stackable' });
          }}
          disabled={isCreatingForm}
          style={{flex: '1 1 220px', marginBottom: 0, background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)'}}
        >
          {isCreatingForm && creationMode === 'stackable' ? 'Creating stackable…' : '🗂️ Create Stackable Form'}
        </button>
        <button
          className="create-form-btn"
          onClick={() => openImportExportModal()}
          style={{
            flex: '1 1 220px', 
            marginBottom: 0, 
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white'
          }}
        >
          📊 Import/Export
        </button>
      </div>

      <div className="forms-grid">
        {filteredForms.map((form) => (
          <div key={form.id} className="form-card">
            <div className="form-card-header">
              <h3 className="form-card-title">{form.title}</h3>
              <div className="form-signature">Form Signature #: {form.signature}</div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: form.formType === 'stackable' ? '#0ea5e9' : '#38a169' }}>
                {form.formType === 'stackable' ? '🗂️ Stackable Form' : '📝 Standard Form'}
              </div>
              <div style={{ fontSize: '12px', color: form.isPublic === false && form.accessPasswordHash ? '#e53e3e' : '#38a169' }}>
                {form.isPublic === false && form.accessPasswordHash ? '🔒 Password Protected' : '🌐 Public'}
              </div>
              <a href={`/form/${user?.uid}/${form.id}`} className="form-link" target="_blank" rel="noopener noreferrer">
                🔗 Public Link
              </a>
            </div>
            <div className="form-card-actions">
              <button 
                className="edit-btn"
                onClick={() => {
                  fetchFormData(form.id);
                  setCurrentView('edit');
                }}
              >
                ✏️ Edit Form
              </button>
              <button 
                className="submissions-btn"
                onClick={() => setCurrentView('submissions')}
              >
                👁️ View Submissions
              </button>
              <button 
                className="export-btn"
                onClick={() => openImportExportModal(form.id)}
                style={{backgroundColor: '#8b5cf6', color: 'white'}}
                title="Export this form"
              >
                📤 Export
              </button>
              <button 
                className="delete-btn"
                onClick={() => deleteForm(form.id)}
                style={{backgroundColor: '#e53e3e', color: 'white'}}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
        
        {filteredForms.length === 0 && (
          <div className="form-card" style={{gridColumn: '1 / -1', textAlign: 'center', padding: '60px'}}>
            <h3 style={{color: '#718096', marginBottom: '20px'}}>
              {forms.length === 0 ? 'No forms created yet' : 'No forms found'}
            </h3>
            <button 
              className="create-form-btn"
              onClick={createNewForm}
              style={{maxWidth: '200px'}}
              disabled={isCreatingForm}
            >
              {isCreatingForm ? 'Creating…' : '➕ Create Your First Form'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditForm = () => (
    <div className="main-content">
      <div className="main-header">
        <h2>✏️ Edit Form</h2>
        <div className="header-actions">
          <button 
            className="edit-btn"
            onClick={() => setCurrentView('dashboard')}
          >
            ← Back to Forms
          </button>
        </div>
      </div>
      
      <div style={{display: 'flex', gap: '30px'}}>
        <div style={{flex: '1'}}>
          <div className="form-card" style={{marginBottom: '25px'}}>
            <h3>Form Settings</h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: '600'}}>Form Title</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={handleTitleChange}
                  placeholder="Enter Form Title"
                  style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                />
              </div>
              
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: '600'}}>Webhook URL</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="Enter Webhook URL"
                  style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                />
                <button onClick={updateWebhookUrl} className="edit-btn" style={{marginTop: '10px', padding: '8px 16px'}}>
                  Save Webhook
                </button>
              </div>

              <div>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: '600'}}>Form Access</label>
                <select
                  value={isPasswordProtected ? 'protected' : 'public'}
                  onChange={(e) => {
                    const isProtected = e.target.value === 'protected';
                    setIsPasswordProtected(isProtected);
                    if (!isProtected) {
                      setPasswordInput('');
                    }
                  }}
                  style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '10px'}}
                >
                  <option value="public">Public - no password required</option>
                  <option value="protected">Password protected</option>
                </select>

                {isPasswordProtected && (
                  <div style={{marginBottom: '10px'}}>
                    <input
                      type="password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      placeholder={hasStoredPassword ? 'Enter new password (leave blank to keep current)' : 'Enter access password'}
                      style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                    />
                    <p style={{fontSize: '12px', color: '#64748b', marginTop: '6px'}}>
                      {hasStoredPassword ? 'A password is already set. Leave blank to keep it unchanged.' : 'This password will be required before the form can be viewed.'}
                    </p>
                  </div>
                )}

                <button onClick={saveAccessSettings} className="edit-btn" style={{padding: '8px 16px'}}>
                  Save Access Settings
                </button>
              </div>
              
              <div>
                <label style={{display: 'block', marginBottom: '10px', fontWeight: '600'}}>
                  <input
                    type="checkbox"
                    checked={showReferenceNumber}
                    onChange={(e) => setShowReferenceNumber(e.target.checked)}
                    style={{marginRight: '8px'}}
                  />
                  Show Reference Number on Form
                </label>
                <button onClick={updateReferenceNumberSetting} className="edit-btn" style={{padding: '8px 16px'}}>
                  Save Setting
                </button>
                <p style={{fontSize: '12px', color: '#64748b', marginTop: '5px'}}>
                  When enabled, users will see the auto-generated reference number on the form. When disabled, the reference number is still generated but hidden from users.
                </p>
              </div>
              
              <div>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: '600'}}>Style Theme</label>
                <select 
                  value={selectedStyle} 
                  onChange={(e) => setSelectedStyle(e.target.value)}
                  style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                >
                  <option value="default">Default Style</option>
                  <option value="style1">Style 1</option>
                  <option value="style2">Style 2</option>
                  <option value="style3">Style 3</option>
                </select>
              </div>

              <div>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: '600'}}>Submission Rule</label>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontSize: '14px', color: '#1e293b'}}>
                  <input
                    type="checkbox"
                    checked={requireAllFields}
                    onChange={(e) => updateRequireAllFieldsSetting(e.target.checked)}
                    style={{width: '18px', height: '18px'}}
                  />
                  Require every question to be completed before submission
                </label>
                <p style={{fontSize: '12px', color: '#64748b', marginTop: '5px'}}>
                  Turn this off if you only need a few key answers and want to let users skip the rest.
                </p>
              </div>
            </div>
          </div>
          
          <div className="form-card">
            <h3>Add Form Fields</h3>

            <div style={{marginBottom: '20px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
                <h4 style={{margin: 0, fontSize: '16px', color: '#1e293b'}}>Form Structure</h4>
                <div style={{display: 'flex', gap: '8px'}}>
                  <button
                    type="button"
                    onClick={() => handleFormTypeChange('single')}
                    className={formType === 'single' ? 'edit-btn' : 'submissions-btn'}
                    style={{padding: '6px 14px'}}
                  >
                    Single form
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormTypeChange('stackable')}
                    className={formType === 'stackable' ? 'edit-btn' : 'submissions-btn'}
                    style={{padding: '6px 14px'}}
                  >
                    Stackable form
                  </button>
                </div>
              </div>
              <p style={{fontSize: '12px', color: '#475569', marginTop: '10px', marginBottom: '0'}}>
                Stackable forms group multiple subforms under one link. End users pick the subform they need from a dropdown on the live form.
              </p>
            </div>

            {formType === 'stackable' && (
              <div style={{marginBottom: '20px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <h4 style={{margin: 0, fontSize: '16px', color: '#1e293b'}}>Subforms</h4>
                  <button
                    type="button"
                    onClick={handleAddVariant}
                    className="create-form-btn"
                    style={{padding: '6px 12px', fontSize: '13px'}}
                  >
                    ➕ Add Subform
                  </button>
                </div>
                {variants.length === 0 ? (
                  <p style={{fontSize: '13px', color: '#475569', margin: 0}}>
                    No subforms yet. Add one to start configuring this stackable form.
                  </p>
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {variants.map((variant) => (
                      <div key={variant.id} style={{display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center'}}>
                        <button
                          type="button"
                          onClick={() => handleVariantSelect(variant.id)}
                          className={variant.id === activeVariantId ? 'edit-btn' : 'submissions-btn'}
                          style={{flex: '1 1 auto'}}
                        >
                          {variant.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRenameVariant(variant.id)}
                          className="submissions-btn"
                          style={{padding: '6px 10px'}}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteVariant(variant.id)}
                          className="delete-btn"
                          style={{padding: '6px 10px', backgroundColor: '#ef4444', color: 'white'}}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PayFast Payment Settings Section */}
            {((formType === 'stackable' && activeVariantId) || formType === 'single') && (
              <div style={{marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                <h5 style={{margin: '0 0 12px 0', fontWeight: '600', color: '#1e293b'}}>
                  💳 PayFast Payment Settings
                  {formType === 'stackable' && ` - ${variants.find((variant) => variant.id === activeVariantId)?.name || 'Current Subform'}`}
                </h5>
                
                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', fontSize: '14px', color: '#1e293b'}}>
                    <input
                      type="checkbox"
                      checked={variantPaymentSettings[activeVariantId || 'global']?.enabled || false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const variantKey = activeVariantId || 'global';
                        setVariantPaymentSettings(prev => ({
                          ...prev,
                          [variantKey]: {
                            ...prev[variantKey],
                            enabled,
                            url: enabled ? prev[variantKey]?.url || '' : ''
                          }
                        }));
                      }}
                      style={{width: '18px', height: '18px'}}
                    />
                    Enable PayFast payments for this {formType === 'stackable' ? 'subform' : 'form'}
                  </label>
                  <p style={{fontSize: '12px', color: '#64748b', marginTop: '5px', marginLeft: '28px'}}>
                    When enabled, the submit button will change to "Pay Now" and redirect to your payment URL.
                  </p>
                </div>

                {variantPaymentSettings[activeVariantId || 'global']?.enabled && (
                  <div style={{marginBottom: '15px'}}>
                    <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600'}}>
                      Payment URL
                    </label>
                    <input
                      type="url"
                      value={variantPaymentSettings[activeVariantId || 'global']?.url || ''}
                      onChange={(e) => {
                        const variantKey = activeVariantId || 'global';
                        setVariantPaymentSettings(prev => ({
                          ...prev,
                          [variantKey]: {
                            ...prev[variantKey],
                            url: e.target.value
                          }
                        }));
                      }}
                      placeholder="https://your-payment-link.com"
                      style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px'}}
                    />
                    <p style={{fontSize: '12px', color: '#64748b', marginTop: '5px'}}>
                      Enter the URL where users should be redirected to complete payment.
                    </p>
                  </div>
                )}

                <button 
                  onClick={() => {
                    const variantKey = activeVariantId || 'global';
                    const settings = variantPaymentSettings[variantKey];
                    saveVariantPaymentSettings(variantKey, settings?.enabled || false, settings?.url || '');
                  }}
                  className="edit-btn" 
                  style={{padding: '8px 16px', marginTop: '10px'}}
                >
                  Save Payment Settings
                </button>
              </div>
            )}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '20px'}}>
              <button onClick={() => setNewFieldType('text')} className={newFieldType === 'text' ? 'edit-btn' : 'submissions-btn'}>Text</button>
              <button onClick={() => setNewFieldType('email')} className={newFieldType === 'email' ? 'edit-btn' : 'submissions-btn'}>Email</button>
              <button onClick={() => setNewFieldType('number')} className={newFieldType === 'number' ? 'edit-btn' : 'submissions-btn'}>Number</button>
              <button onClick={() => setNewFieldType('date')} className={newFieldType === 'date' ? 'edit-btn' : 'submissions-btn'}>Date</button>
              <button onClick={() => setNewFieldType('checkbox')} className={newFieldType === 'checkbox' ? 'edit-btn' : 'submissions-btn'}>Checkbox</button>
              <button onClick={() => setNewFieldType('dropdown')} className={newFieldType === 'dropdown' ? 'edit-btn' : 'submissions-btn'}>Dropdown</button>
              <button onClick={() => setNewFieldType('query')} className={newFieldType === 'query' ? 'edit-btn' : 'submissions-btn'}>🔍 Query API</button>
            </div>
            
            <input
              type="text"
              placeholder="Field Label"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              style={{width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '15px'}}
            />
            
            {newFieldType === 'dropdown' && (
              <div style={{marginBottom: '15px'}}>
                {dropdownOptions.map((option, index) => (
                  <div key={index} style={{marginBottom: '12px', backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0'}}>
                    <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#1e293b', marginBottom: '4px'}}>
                      Option {index + 1} Label
                    </label>
                    <input
                      type="text"
                      value={option.label || ''}
                      onChange={(e) => handleDropdownOptionChange(index, 'label', e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      style={{width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', marginBottom: '8px'}}
                    />
                    <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#1e293b', marginBottom: '4px'}}>
                      Helper Note (shown after selecting)
                    </label>
                    <textarea
                      value={option.note || ''}
                      onChange={(e) => handleDropdownOptionChange(index, 'note', e.target.value)}
                      placeholder="Optional instructions for this choice"
                      style={{width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', minHeight: '60px', fontSize: '13px'}}
                    />
                  </div>
                ))}
                <button onClick={addDropdownOption} className="submissions-btn" style={{padding: '8px 16px'}}>
                  Add Option
                </button>
              </div>
            )}
            
            {newFieldType === 'query' && (
              <div style={{marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px'}}>
                <h4 style={{marginBottom: '10px', color: '#2d3748'}}>🔍 API Query Configuration</h4>
                
                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px'}}>API URL</label>
                  <input
                    type="url"
                    value={queryApiUrl}
                    onChange={(e) => setQueryApiUrl(e.target.value)}
                    placeholder="https://api.clickup.com/api/v2/list/LIST_ID/task"
                    style={{width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px'}}
                  />
                </div>
                
                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px'}}>API Headers (JSON)</label>
                  <textarea
                    value={queryApiHeaders}
                    onChange={(e) => setQueryApiHeaders(e.target.value)}
                    placeholder='{"Authorization": "Bearer YOUR_TOKEN", "Content-Type": "application/json"}'
                    style={{width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', minHeight: '60px', fontFamily: 'monospace'}}
                  />
                </div>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                  <div>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px'}}>Data Path</label>
                    <input
                      type="text"
                      value={queryDataPath}
                      onChange={(e) => setQueryDataPath(e.target.value)}
                      placeholder="data.tasks"
                      style={{width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px'}}>Display Field</label>
                    <input
                      type="text"
                      value={queryDisplayField}
                      onChange={(e) => setQueryDisplayField(e.target.value)}
                      placeholder="name"
                      style={{width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px'}}
                    />
                  </div>
                </div>
                
                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px'}}>Value Field (optional)</label>
                  <input
                    type="text"
                    value={queryValueField}
                    onChange={(e) => setQueryValueField(e.target.value)}
                    placeholder="id"
                    style={{width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px'}}
                  />
                </div>
                
                <div style={{padding: '10px', backgroundColor: '#e2e8f0', borderRadius: '6px', fontSize: '12px', color: '#4a5568'}}>
                  <strong>How it works:</strong><br/>
                  • User types and it queries your API for matching results<br/>
                  • <strong>Data Path:</strong> Path to array in API response (e.g., "data.tasks")<br/>
                  • <strong>Display Field:</strong> What users see (e.g., "name", "title")<br/>
                  • <strong>Value Field:</strong> What gets saved (e.g., "id", "custom_id")
                </div>
              </div>
            )}
            
            <button onClick={addField} className="create-form-btn">Add Field</button>
          </div>
        </div>
        
        <div style={{flex: '1'}}>
          <div className="form-card">
            <h3>Form Preview</h3>
            <div style={{padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', minHeight: '400px'}}>
              <h4>{formTitle || 'Untitled Form'}</h4>
              {formType === 'stackable' && activeVariantId && (
                <p style={{fontSize: '13px', color: '#475569', marginTop: '4px'}}>
                  Previewing subform:{' '}
                  <strong>{variants.find((variant) => variant.id === activeVariantId)?.name || 'Unnamed subform'}</strong>
                </p>
              )}
              
              {fields.map((field, index) => (
                <div key={index} style={{marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '600'}}>
                    {field.label}
                    {requireAllFields ? (
                      <span style={{fontSize: '14px', color: '#ef4444'}}>*</span>
                    ) : (
                      <span style={{fontSize: '12px', color: '#64748b', fontWeight: '500'}}>(Optional)</span>
                    )}
                  </label>
                  {field.type === 'dropdown' ? (
                    (() => {
                      const normalizedOptions = (field.options || []).map((option) =>
                        typeof option === 'string'
                          ? { label: option, note: '' }
                          : {
                              label: option?.label || '',
                              note: option?.note || ''
                            }
                      ).filter((option) => option.label);

                      return (
                        <>
                          <select style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}>
                            <option>Select an option</option>
                            {normalizedOptions.map((option, idx) => (
                              <option key={idx} value={option.label}>{option.label}</option>
                            ))}
                          </select>
                          {normalizedOptions.some((option) => option.note) && (
                            <div style={{marginTop: '8px', fontSize: '12px', color: '#475569', backgroundColor: '#f8fafc', borderRadius: '6px', padding: '8px', border: '1px dashed #cbd5f5'}}>
                              <strong style={{display: 'block', marginBottom: '4px', color: '#1e293b'}}>Helper notes:</strong>
                              {normalizedOptions.map((option, idx) => (
                                option.note ? (
                                  <div key={idx} style={{marginBottom: '4px'}}>
                                    <span style={{fontWeight: 600}}>{option.label || `Option ${idx + 1}`}:</span> {option.note}
                                  </div>
                                ) : null
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : field.type === 'query' ? (
                    <div>
                      <input 
                        type="text" 
                        placeholder={`Type to search ${field.label.toLowerCase()}...`} 
                        style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                      />
                      <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                        🔍 Query API: {field.apiConfig?.url || 'Not configured'}
                      </div>
                    </div>
                  ) : (
                    <input 
                      type={field.type} 
                      placeholder={field.label} 
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px'}}
                    />
                  )}
                  <div style={{marginTop: '10px', display: 'flex', gap: '8px'}}>
                    <button
                      type="button"
                      onClick={() => moveField(index, -1)}
                      disabled={index === 0}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        border: '1px solid #a0aec0',
                        backgroundColor: index === 0 ? '#e2e8f0' : '#edf2f7',
                        color: index === 0 ? '#94a3b8' : '#2d3748',
                        cursor: index === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ↑ Move Up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveField(index, 1)}
                      disabled={index === fields.length - 1}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        border: '1px solid #a0aec0',
                        backgroundColor: index === fields.length - 1 ? '#e2e8f0' : '#edf2f7',
                        color: index === fields.length - 1 ? '#94a3b8' : '#2d3748',
                        cursor: index === fields.length - 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ↓ Move Down
                    </button>
                    <button type="button" onClick={() => editField(index)} style={{padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#f8f9fa'}}>Edit</button>
                    <button type="button" onClick={() => removeField(index)} style={{padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e53e3e', backgroundColor: '#fff5f5', color: '#e53e3e'}}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSubmissions = () => (
    <div className="main-content">
      <div className="main-header">
        <h2>📄 Form Submissions</h2>
        <div className="header-actions">
          <button 
            className="edit-btn"
            onClick={() => setCurrentView('dashboard')}
          >
            ← Back to Forms
          </button>
          {submissions.length > 0 && (
            <button 
              onClick={() => submissions.forEach(sub => generateSubmissionPDF(sub))}
              className="create-form-btn"
            >
              📥 Download All PDFs
            </button>
          )}
        </div>
      </div>
      
      {submissions.length > 0 ? (
        <div className="forms-grid">
          {submissions.map((sub) => {
            const submissionDate = new Date(sub.submittedAt);
            const formattedDate = submissionDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
            const referenceNumber = sub['Form Reference Number'] || sub.id;
            
            return (
              <div key={sub.id} className="form-card">
                <div className="form-card-header">
                  <h3 className="form-card-title">📄 {referenceNumber}</h3>
                  <div className="form-signature">
                    {formattedDate} at {submissionDate.toLocaleTimeString()}
                  </div>
                </div>
                <div style={{marginBottom: '20px'}}>
                      {(() => {
                        const labelMap = sub[LABEL_MAP_KEY] || {};
                        return Object.entries(sub)
                          .filter(([key]) => !['id', 'submittedAt', 'formTitle', 'Form Reference Number', LABEL_MAP_KEY].includes(key))
                          .slice(0, 4)
                          .map(([key, value]) => {
                            const displayKey = labelMap[key] || key;
                            const displayValue = value ? value.toString() : 'N/A';
                            const shortened = displayValue.length > 50 ? `${displayValue.substring(0, 50)}...` : displayValue;
                            return (
                              <div key={key} style={{marginBottom: '8px', fontSize: '14px'}}>
                                <strong>{displayKey}:</strong> {shortened}
                              </div>
                            );
                          });
                      })()}
                </div>
                <button 
                  onClick={() => generateSubmissionPDF(sub)}
                  className="submissions-btn"
                  style={{width: '100%'}}
                >
                  📥 Download PDF
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="form-card" style={{textAlign: 'center', padding: '60px'}}>
          <h3 style={{color: '#718096', marginBottom: '20px'}}>No submissions yet</h3>
          <p style={{color: '#a0aec0', marginBottom: '30px'}}>Share your form link to start receiving submissions!</p>
          <a href={`/form/${user?.uid}`} target="_blank" rel="noopener noreferrer" className="create-form-btn" style={{display: 'inline-block', textDecoration: 'none'}}>
            🔗 Open Public Form
          </a>
        </div>
      )}
    </div>
  );

  const renderAccount = () => {
    const currentPlan = PLAN_CONFIG[userPlan.plan] || PLAN_CONFIG[DEFAULT_PLAN];
    const currentTier = userPlan.plan || 'FREE';
    const billingStatus = userPlan.billingStatus || 'active';
    
    return (
      <div className="main-content">
        <div className="content-header">
          <h1>Your Account & Subscription</h1>
          <p>Manage your subscription and upgrade/downgrade tiers</p>
          <button 
            onClick={refreshUserPlan}
            disabled={planLoading}
            style={{
              background: '#48bb78',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            {planLoading ? 'Refreshing...' : '🔄 Refresh Account'}
          </button>
        </div>

        {/* Current Tier Highlight */}
        <div className="form-card" style={{marginBottom: '30px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
          <div style={{padding: '20px', textAlign: 'center'}}>
            <h2 style={{margin: '0 0 10px 0', color: 'white'}}>Your Current Tier</h2>
            <div style={{fontSize: '32px', fontWeight: 'bold', margin: '10px 0'}}>{currentPlan.name}</div>
            <div style={{fontSize: '18px', opacity: '0.9'}}>
              {isUnlimitedLimit(currentPlan.limit) ? 'Unlimited submissions per month' : `${formatLimitLabel(currentPlan.limit)} submissions per month`}
            </div>
            <div style={{fontSize: '24px', fontWeight: 'bold', margin: '15px 0 5px 0'}}>
              {currentPlan.priceMonthly === 0 ? 'FREE' : `R${currentPlan.priceMonthly}/month`}
            </div>
            <div style={{fontSize: '14px', opacity: '0.8'}}>
              Status: {billingStatus} • This Month: {limitInfo.usageCount} submissions used
            </div>
          </div>
        </div>

        {/* All Tiers Grid */}
        <div className="form-card">
          <h2 style={{textAlign: 'center', marginBottom: '30px'}}>Choose Your Tier</h2>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px'}}>
            {PLAN_ORDER.map((planKey) => {
              const plan = PLAN_CONFIG[planKey];
              const isCurrentTier = currentTier === planKey;
              const isProcessing = processingPlan === planKey;
              const canUpgrade = planKey !== currentTier;
              
              // Determine button text and action
              let buttonText = 'Select This Tier';
              let buttonAction = () => upgradeTier(planKey);
              let buttonColor = '#3182ce';
              
              if (isCurrentTier) {
                buttonText = 'Current Tier';
                buttonAction = null;
                buttonColor = '#48bb78';
              } else if (isProcessing) {
                buttonText = 'Processing...';
                buttonAction = null;
                buttonColor = '#718096';
              } else if (planKey === 'FREE') {
                buttonText = 'Downgrade to FREE';
                buttonColor = '#e53e3e';
              } else {
                buttonText = `Upgrade to ${plan.name}`;
                buttonColor = '#3182ce';
              }
              
              return (
                <div 
                  key={planKey} 
                  style={{
                    border: isCurrentTier ? '3px solid #48bb78' : '2px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '24px',
                    background: isCurrentTier ? 'linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%)' : 'white',
                    position: 'relative',
                    boxShadow: isCurrentTier ? '0 8px 25px rgba(72, 187, 120, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    transform: isCurrentTier ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isCurrentTier && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#48bb78',
                      color: 'white',
                      padding: '6px 20px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 8px rgba(72, 187, 120, 0.3)'
                    }}>
                      ✓ ACTIVE
                    </div>
                  )}
                  
                  <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <h3 style={{
                      margin: '0 0 10px 0', 
                      fontSize: '22px', 
                      fontWeight: 'bold',
                      color: isCurrentTier ? '#2d3748' : '#4a5568'
                    }}>
                      {plan.name}
                    </h3>
                    
                    <div style={{
                      fontSize: '36px', 
                      fontWeight: 'bold', 
                      color: isCurrentTier ? '#48bb78' : '#2d3748',
                      marginBottom: '8px'
                    }}>
                      {plan.priceMonthly === 0 ? 'FREE' : `R${plan.priceMonthly}`}
                    </div>
                    
                    {plan.priceMonthly > 0 && (
                      <div style={{fontSize: '14px', color: '#718096', marginBottom: '15px'}}>
                        per month
                      </div>
                    )}
                    
                    <div style={{
                      fontSize: '16px', 
                      color: '#4a5568',
                      padding: '10px',
                      background: isCurrentTier ? 'rgba(72, 187, 120, 0.1)' : 'rgba(74, 85, 104, 0.1)',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      {isUnlimitedLimit(plan.limit) ? 
                        '🚀 Unlimited submissions' : 
                        `📊 ${formatLimitLabel(plan.limit)} submissions/month`
                      }
                    </div>
                  </div>
                  
                  {/* Features list */}
                  <div style={{marginBottom: '25px'}}>
                    <ul style={{
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      fontSize: '14px',
                      color: '#4a5568'
                    }}>
                      <li style={{marginBottom: '8px', display: 'flex', alignItems: 'center'}}>
                        <span style={{color: '#48bb78', marginRight: '8px'}}>✓</span>
                        Form Builder
                      </li>
                      <li style={{marginBottom: '8px', display: 'flex', alignItems: 'center'}}>
                        <span style={{color: '#48bb78', marginRight: '8px'}}>✓</span>
                        Submission Management
                      </li>
                      <li style={{marginBottom: '8px', display: 'flex', alignItems: 'center'}}>
                        <span style={{color: '#48bb78', marginRight: '8px'}}>✓</span>
                        Data Export
                      </li>
                      {plan.priceMonthly > 0 && (
                        <>
                          <li style={{marginBottom: '8px', display: 'flex', alignItems: 'center'}}>
                            <span style={{color: '#48bb78', marginRight: '8px'}}>✓</span>
                            Priority Support
                          </li>
                          <li style={{marginBottom: '8px', display: 'flex', alignItems: 'center'}}>
                            <span style={{color: '#48bb78', marginRight: '8px'}}>✓</span>
                            Advanced Features
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                  
                  <button
                    onClick={buttonAction}
                    disabled={!buttonAction || isProcessing}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: buttonAction && !isProcessing ? 'pointer' : 'not-allowed',
                      background: buttonColor,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      opacity: (!buttonAction || isProcessing) ? 0.6 : 1,
                      transition: 'all 0.3s ease',
                      boxShadow: buttonAction && !isProcessing ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'
                    }}
                  >
                    {buttonText}
                  </button>
                  
                  {planKey !== 'FREE' && !isCurrentTier && (
                    <div style={{
                      fontSize: '12px', 
                      color: '#718096', 
                      textAlign: 'center',
                      marginTop: '8px'
                    }}>
                      Secure payment via PayFast
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Info */}
        <div className="form-card" style={{marginTop: '30px', background: '#f7fafc'}}>
          <h3>How It Works</h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '15px'}}>
            <div>
              <strong>1. Choose Your Tier</strong>
              <p style={{fontSize: '14px', color: '#4a5568', margin: '5px 0 0 0'}}>
                Select the tier that fits your needs
              </p>
            </div>
            <div>
              <strong>2. Secure Payment</strong>
              <p style={{fontSize: '14px', color: '#4a5568', margin: '5px 0 0 0'}}>
                Pay securely through PayFast (South African payment processor)
              </p>
            </div>
            <div>
              <strong>3. Instant Upgrade</strong>
              <p style={{fontSize: '14px', color: '#4a5568', margin: '5px 0 0 0'}}>
                Your account is upgraded immediately after payment
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard">
      {renderLimitOverlay()}
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h1>FormBuilder Mk3</h1>
          <div className="user-info">
            <div><strong>User:</strong> {user?.email || 'info@speakerrepairs.co.za'}</div>
            <div><strong>Role:</strong> user</div>
          </div>
        </div>
        
        <div className="sidebar-content">
          <button 
            className="create-form-btn"
            onClick={() => {
              const planKey = userPlan.plan || DEFAULT_PLAN;
              const formLimit = PLAN_CONFIG[planKey]?.formLimit || 3;
              if (forms.length >= formLimit) {
                alert(`You have reached your form limit of ${formLimit} for the ${planKey} plan. Please upgrade to create more forms.`);
                return;
              }
              createNewForm();
            }}
            disabled={isCreatingForm}
          >
            {isCreatingForm && creationMode !== 'stackable' ? 'Creating…' : '➕ Create Standard Form'}
          </button>
          <button
            className="create-form-btn"
            onClick={() => {
              const planKey = userPlan.plan || DEFAULT_PLAN;
              const formLimit = PLAN_CONFIG[planKey]?.formLimit || 3;
              if (forms.length >= formLimit) {
                alert(`You have reached your form limit of ${formLimit} for the ${planKey} plan. Please upgrade to create more forms.`);
                return;
              }
              createNewForm({ formType: 'stackable' });
            }}
            disabled={isCreatingForm}
            style={{marginTop: '10px', backgroundColor: '#0ea5e9'}}
          >
            {isCreatingForm && creationMode === 'stackable' ? 'Creating stackable…' : '🗂️ Create Stackable Form'}
          </button>
          <p style={{fontSize: '12px', color: '#e2e8f0', marginTop: '6px', marginBottom: '18px'}}>
            Opens a stackable form in the editor with a starter subform.
          </p>
          
          <div className="sidebar-section">
            <div 
              className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={() => setCurrentView('dashboard')}
            >
              <span className="icon">📋</span>
              My Forms
            </div>
            
            <div 
              className={`nav-item ${currentView === 'submissions' ? 'active' : ''}`}
              onClick={() => setCurrentView('submissions')}
            >
              <span className="icon">📄</span>
              Submissions ({submissions.length})
            </div>

            <div 
              className="nav-item"
              onClick={() => navigate('/Accounts')}
            >
              <span className="icon">👤</span>
              Account
            </div>
          </div>
          
          <button 
            onClick={handleLogout}
            className="logout-button"
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      {currentView === 'dashboard' && renderDashboard()}
      {currentView === 'edit' && renderEditForm()}
      {currentView === 'submissions' && renderSubmissions()}
      {currentView === 'account' && renderAccount()}

      {/* Import/Export Modal */}
      {showImportExportModal && (
        <FormImportExport
          user={user}
          forms={forms}
          onImportComplete={handleFormsUpdated}
          onClose={closeImportExportModal}
          currentFormId={importExportSelectedFormId}
        />
      )}
    </div>
  );
};

export default Dashboard;